import React, {useEffect, useContext, useState} from 'react'
import {Button, ButtonGroup, Grid, IconButton, Slider, Box, Modal} from '@material-ui/core'
import {useHistory} from 'react-router-dom'
import {ReservationCalendarMainContext} from '../../context/ReservationCalendarMain'
import AddRoundedIcon from '@material-ui/icons/AddRounded'
import RemoveRoundedIcon from '@material-ui/icons/RemoveRounded'
import {MdDateRange} from 'react-icons/md'
import './Navigation.scss'
import ReservationReserve from '../ReservationReserve/ReservationReserve'
import StepConfirmedReservation from '../ReservationReserve/ReservationSteps/StepConfirmedReservation'
import {ReservationContext} from '../../context/Reservation'
import moment from 'moment'
import useReservationGanttContext from '../../context/ReservationGantt/ReservationGantt'
import clsx from 'clsx'
import {UserContext} from '../../context/User'
import PurchaseReservationPopup from '../ReservationConfiguration/PurchaseReservationPopup'
import {SettingsContext} from '../../context/Settings/Settings'

const classRoot = 'c-res-navigation'

const Navigation = () => {
  const {
    calendarView,
    setCalendarView,
    setSelectedDate,
    selectedDate,
    sliderLength,
    setSliderLength,
    hasEnabledConfiguration,
  } = useContext(ReservationCalendarMainContext)

  const settings = useContext(SettingsContext)
  const currentUser = useContext(UserContext)
  const [purchaseShow, setPurchaseShow] = React.useState(false)

  const {
    reservation,
    setReservation,
    clearReservation,
    configuration,
    clearReservationsData,
    popReserve,
    setPopReserve,
  } = useContext(ReservationContext)
  const {setStartDateSec} = useReservationGanttContext()
  const [popConfirm, setPopConfirm] = useState(false)
  const [reservationId, setReservationId] = useState(null)
  const sliderStep = 1 //60 * 60 * 24
  const minSlider = 1 //60 * 60 * 24
  const maxSlider = 100 //60 * 60 * 24 * 30
  const history = useHistory()

  const handleChange = event => {
    if (event.currentTarget.value === 'today') {
      setSelectedDate(moment())
    } else if (event.currentTarget.value === 'gantt') {
      switch (calendarView) {
        case 'day':
          // day view > switching Gantt will show hourly
          setSliderLength(66)
          break
        case 'week':
          // week view > switching Gantt will show daily
          setSliderLength(33)
          break
        case 'month':
          // month view > switching Gantt will show monthly
          setSliderLength(1)
          break
        default:
        // n/a
      }
      setCalendarView(event.currentTarget.value)
      setStartDateSec(selectedDate.valueOf())
    } else if (event.currentTarget.value === 'reservation') {
      if (currentUser.details.reservation_enabled) {
        setCalendarView(event.currentTarget.value)
      } else {
        setPurchaseShow(true)
      }
    } else {
      setCalendarView(event.currentTarget.value)
    }
  }

  const handleClosePurchaseModal = e => {
    setPurchaseShow(false)
  }

  const handleViewSecLengthChange = (e, v) => {
    const day = 60 * 60 * 24
    const s = v - day // subtract 1 day
    setSliderLength(v)
  }

  const handleAddSlider = e => {
    const newLength = Math.min(sliderLength + 10, 100)
    if (newLength > sliderStep * 100) {
      return
    } else {
      setSliderLength(newLength)
    }
  }

  const handleSubtractSlider = e => {
    const newLength = Math.max(sliderLength - 10, 0)
    if (newLength < sliderStep) {
      return
    } else {
      setSliderLength(newLength)
    }
  }

  const handleClose = e => {
    setPopReserve(false)
    clearReservation()
    clearReservationsData()
  }

  const handleConfirmClose = e => {
    setPopConfirm(false)
    clearReservationsData()
  }

  const handleReserve = id => {
    setPopReserve(false)
    setPopConfirm(true)
    setReservationId(id)
    clearReservation()
  }

  const handleReserveButtonClick = e => {
    if (currentUser.details.reservation_enabled) {
      setPopReserve(true)
    } else {
      setPurchaseShow(true)
    }
  }

  const {push: h_push, location: h_path} = history
  useEffect(() => {
    if (calendarView === 'gantt') {
      if (h_path.pathname !== '/reservations/gantt') {
        h_push('/reservations/gantt')
      }
    } else if (calendarView === 'reservation') {
      if (h_path.pathname !== '/reservations/bookings') h_push({pathname: '/reservations/bookings'})
    } else {
      if (h_path.pathname !== '/reservations/calendar') h_push({pathname: '/reservations/calendar'})
    }
  }, [calendarView, h_push, h_path])

  const canViewReservation =
    (settings.data?.user_access_rules?.can_view?.reservations || []).length > 0

  const canReserve = (settings.data?.user_access_rules?.can_add?.reservations || []).length > 0

  return (
    <div className={`${classRoot} c-res-mod`}>
      <div className={`${classRoot}__left-section`}>
        <div className={`${classRoot}__header-title`}>Calendar</div>
      </div>
      <div className={`${classRoot}__right-section ap-flex ap-flex--align-center`}>
        <Button className={`${classRoot}__btn-today`} value="today" onClick={handleChange}>
          <MdDateRange style={{marginRight: '10px'}} /> Today
        </Button>
        <div
          className={`${classRoot}__view-group c-res-mod ap-button-group`}
          size="small"
          aria-label="small outlined primary button group"
        >
          <button
            className={clsx(
              `ap-button-group__item`,
              `${calendarView === 'day' ? 'ap-button-group__item--active' : ''}`
            )}
            value="day"
            onClick={handleChange}
          >
            Day
          </button>
          <button
            className={clsx(
              `ap-button-group__item`,
              `${calendarView === 'week' ? 'ap-button-group__item--active' : ''}`
            )}
            value="week"
            onClick={handleChange}
          >
            Week
          </button>
          <button
            className={clsx(
              `ap-button-group__item`,
              `${calendarView === 'month' ? 'ap-button-group__item--active' : ''}`
            )}
            value="month"
            onClick={handleChange}
          >
            Month
          </button>
          <button
            className={clsx(
              `ap-button-group__item`,
              `${calendarView === 'gantt' ? 'ap-button-group__item--active' : ''}`
            )}
            value="gantt"
            onClick={handleChange}
          >
            Gantt
          </button>

          <button
            className={clsx(
              `ap-button-group__item`,
              `${calendarView === 'reservation' ? 'ap-button-group__item--active' : ''}`,
              `${canViewReservation && hasEnabledConfiguration ? '' : 'ap-disabled'}`
            )}
            disabled={!hasEnabledConfiguration}
            hidden={!currentUser?.details?.reservation_enabled}
            value="reservation"
            onClick={handleChange}
          >
            Reservations
          </button>
        </div>
      </div>

      <div className="ap-flex ap-flex--end ap-flex--align-center" style={{paddingLeft: 32}}>
        <div
          className={`${classRoot}__slider-styles ap-flex ap-flex--end ap-flex--align-center`}
          hidden={calendarView !== 'gantt'}
        >
          <div className={`ap-typography ap-typography--xxs`} hidden={calendarView !== 'gantt'}>
            Use the slider to adjust the granularity of Gantt Chart!
          </div>
          <IconButton
            onClick={handleSubtractSlider}
            className={`${classRoot}__btn-icon ap-icon-button ap-icon-button--sm`}
            size="small"
          >
            <RemoveRoundedIcon />
          </IconButton>
          <Slider
            classes={{
              thumb: `${classRoot}__slider-thumb`,
            }}
            value={sliderLength}
            onChange={handleViewSecLengthChange}
            aria-labelledby="continuous-slider"
            style={{width: 64}}
            step={sliderStep} // 6 hours
            min={minSlider} // 6 hours
            max={maxSlider} // 30 days
          />
          <IconButton
            onClick={handleAddSlider}
            className={`${classRoot}__btn-icon ap-icon-button ap-icon-button--sm`}
            size="small"
          >
            <AddRoundedIcon />
          </IconButton>
        </div>
        <Button
          disabled={!hasEnabledConfiguration || !canReserve}
          hidden={!currentUser?.details?.reservation_enabled}
          onClick={e => handleReserveButtonClick(e)}
          className={`${classRoot}__btn-reserve ap-button ap-button--light ap-button--outlined`}
        >
          {`Reserve${
            reservation.selected_items.length ? `(${reservation.selected_items.length})` : ''
          }`}
        </Button>

        <Modal
          style={{position: 'absolute'}}
          open={popReserve}
          onClose={handleClose}
          aria-labelledby="simple-modal-title"
          aria-describedby="simple-modal-description"
        >
          <ReservationReserve
            fromGantt={reservation.fromGantt}
            onClose={handleClose}
            handleReserve={handleReserve}
          />
        </Modal>
        <Modal
          style={{position: 'absolute'}}
          open={popConfirm}
          onClose={handleConfirmClose}
          aria-labelledby="simple-modal-title"
          aria-describedby="simple-modal-description"
        >
          <StepConfirmedReservation onClose={handleConfirmClose} />
        </Modal>
      </div>
      <PurchaseReservationPopup show={purchaseShow} onClose={handleClosePurchaseModal} />
    </div>
  )
}

export default Navigation
